// useNotificationActions.js
// Custom hook to handle FCM notification pause/resume actions
// Shared between Header.js and SideBar.js to avoid code duplication

import { useState, useCallback } from "react";
import { useChatStore } from "../store/useChatStore";
import { useAuth } from "../context/AuthContext";
import {
  NOTIFICATION_MODE,
  NOTIFICATION_MODAL_CONTENT,
  NOTIFICATION_STORAGE_KEYS,
  PERMISSION_STATE,
} from "../constants/Notification";
import {
  getFcmTokenFromServiceWorker,
  registerFcmToken,
  unregisterFcmToken,
} from "../utils/fcmOperations";

/**
 * Custom hook for notification pause/resume functionality
 * @returns {Object} - Notification action handlers and state
 */
export const useNotificationActions = () => {
  const { authData } = useAuth();
  const authToken = authData?.systemToken;
  const { toggleRefreshBanner } = useChatStore();

  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // Capture the modal mode when opened to prevent content change during API call
  const [modalMode, setModalMode] = useState(null); // NOTIFICATION_MODE.PAUSE or NOTIFICATION_MODE.RESUME

  // Check if notification feature should be shown
  const showNotificationIcon = process.env.REACT_APP_HIDE_FEATURE === "1";

  // Get current FCM token from localStorage
  const getFcmTokenFromStorage = useCallback(() => {
    return localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
  }, []);

  // Check if user has interacted with notifications
  const hasInteractedWithNotifications = useCallback(() => {
    const fcmToken = localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
    return localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_BANNER_DISMISSED) === "true" || !!fcmToken;
  }, []);

  // Handler for notification icon click - opens modal
  const handleNotificationClick = useCallback(() => {
    // Capture the mode at open time so it doesn't change during the API call
    const fcmToken = localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
    setModalMode(fcmToken ? NOTIFICATION_MODE.PAUSE : NOTIFICATION_MODE.RESUME);
    setShowNotificationModal(true);
  }, []);

  // Handler for modal close
  const handleModalClose = useCallback(() => {
    setShowNotificationModal(false);
    setModalMode(null);
  }, []);

  // Handler for confirming pause (disable notifications)
  const handlePauseConfirm = useCallback(async () => {
    setLoading(true);
    try {
      await unregisterFcmToken(authToken);
    } catch (error) {
      console.error("Failed to pause notifications", error);
    } finally {
      setLoading(false);
      setShowNotificationModal(false);
      toggleRefreshBanner();
      // Reset permission state in the store
      const setPermission = useChatStore.getState().setPermission;
      setPermission(PERMISSION_STATE.DEFAULT);
    }
  }, [authToken, toggleRefreshBanner]);

  // Handler for confirming resume (resume notifications)
  const handleResumeConfirm = useCallback(async () => {
    setLoading(true);
    try {
      // Check if browser permission is already granted (from previous Enable click)
      const currentPermission = Notification.permission;
      
      if (currentPermission !== PERMISSION_STATE.GRANTED) {
        // Need to request permission first
        const result = await Notification.requestPermission();
        if (result !== PERMISSION_STATE.GRANTED) {
          console.warn("Notification permission denied");
          return;
        }
      }
      
      // Browser permission is granted, now try to get FCM token and register
      const newFcmToken = await getFcmTokenFromServiceWorker();
      if (newFcmToken) {
        const success = await registerFcmToken(authToken, newFcmToken);
        if (success) {
          const setPermission = useChatStore.getState().setPermission;
          setPermission(PERMISSION_STATE.GRANTED);
        } else {
          console.error("Failed to register FCM token with server");
        }
      } else {
        console.error("Failed to get FCM token from service worker");
      }
    } catch (error) {
      console.error("Failed to enable notifications", error);
    } finally {
      setLoading(false);
      setShowNotificationModal(false);
      toggleRefreshBanner();
    }
  }, [authToken, toggleRefreshBanner]);

  // Get the appropriate confirm handler based on current state
  const getConfirmHandler = useCallback(() => {
    return modalMode === NOTIFICATION_MODE.PAUSE ? handlePauseConfirm : handleResumeConfirm;
  }, [modalMode, handlePauseConfirm, handleResumeConfirm]);

  // Get modal title based on captured mode (not live localStorage)
  const getModalTitle = useCallback(() => {
    const content = NOTIFICATION_MODAL_CONTENT[modalMode];
    return content?.title || "";
  }, [modalMode]);

  // Get modal message based on captured mode (not live localStorage)
  const getModalMessage = useCallback(() => {
    const content = NOTIFICATION_MODAL_CONTENT[modalMode];
    return content?.message || "";
  }, [modalMode]);

  // Check if notifications are currently enabled
  const isNotificationsEnabled = useCallback(() => {
    return !!localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
  }, []);

  return {
    // State
    showNotificationModal,
    loading,
    showNotificationIcon,
    
    // Handlers
    handleNotificationClick,
    handleModalClose,
    handlePauseConfirm,
    handleResumeConfirm,
    getConfirmHandler,
    
    // Helpers
    getFcmTokenFromStorage,
    hasInteractedWithNotifications,
    isNotificationsEnabled,
    getModalTitle,
    getModalMessage,
  };
};

export default useNotificationActions;
